import { customElement, JSXElement, addstyles, state } from "@tiddlywiki/jsx-lit";
import css from "./app.inline.css";
import warningIcon from "@material-symbols/svg-400/outlined/warning.svg";
import closeIcon from "@material-symbols/svg-400/outlined/close.svg";
import accountCircleIcon from "@material-symbols/svg-400/outlined/account_circle.svg";
import { MaterialSymbol } from "./material-symbol";
import {
  getAllTabs,
  getTab,
  TabId,
  ColumnDefinition,
  FieldDefinition,
  FieldGroupDefinition,
  FieldSection,
  FieldType,
  Mode,
  TabDefinition,
  PermissionRow,
  AdminRecordStore,
  WikiAdminRecord,
  WritablePrefixRow,
  getSectionHeading,
  TemplateTypes,
  IdString,

  KeyFields
} from "./definition/tabs";

import { adminStorage, createDraft, getEmptyItems, jsonReviver } from "./definition/store";
import { definitely, is } from "./definition/utils";
import { logout } from "./passwords";
import { fieldTypeRenderSidebars, formatFieldValue, renderFieldEditor, renderFieldSidebar, renderSwitchField, textWithSlashes } from "./definition/renders";
import { tw5logo } from "./logos";


export type AdminRecord = { id: IdString; };

type ModalMode = "create" | "edit";

type ModalState = {
  [K in TabId]: {
    tabId: K;
    mode: ModalMode;
    draft: AdminRecordStore[K][number];
    /** The unedited original the draft was from. */
    saved: AdminRecordStore[K][number];
    resolverTitle: string;
    operationMessages: Record<string, string>;
    pendingRows: Record<string, number>;
    transientPermissionRows: Record<string, PermissionRow[]>;
    storageError: string;
    loading?: boolean;
  };
}[TabId];

type ModalStateForTab<T extends TabId> = Extract<ModalState, { tabId: T }>;

export interface PerTabFieldState {
  readonly tabId: TabId;
  readonly mode: ModalMode;
  readonly draft: AdminRecord;
  readonly saved: AdminRecord;
  readonly resolverTitle: string;
  readonly operationMessages: Record<string, string>;
  readonly pendingRows: Record<string, number>;
  readonly transientPermissionRows: Record<string, PermissionRow[]>;
  readonly storageError: string;
  readonly loading?: boolean;
}

interface PerTabStore {
  readonly fieldState: PerTabFieldState | null;
  readonly selectedTab: TabDefinition | null;
  readonly itemsByTab: AdminRecordStore;
  readonly displayedStorageError: string;
  readonly isModalLoading: boolean;
  readonly isSaving: boolean;
  readonly isOpeningItem: boolean;
  readonly isOpen: boolean;
  readonly isBusy: boolean;
  readonly clearStorageError: () => void;
  readonly closeModal: () => void;
  readonly openCreate: (tabId: TabId) => void;
  readonly openItem: (tabId: TabId, recordId: IdString) => Promise<void>;
  readonly saveDraft: () => Promise<void>;
  readonly updateDraft: DraftChangeHandler;
  readonly updatePendingRows: PendingRowsChangeHandler;
  readonly updateTransientPermissionRows: PermissionRowsChangeHandler;
  readonly updateResolverTitle: ResolverTitleChangeHandler;
  readonly triggerOperation: OperationTriggerHandler;
}

interface AppStoreState {
  activeTab: TabId;
  itemsByTab: AdminRecordStore;
  isLoadingData: boolean;
}

interface PerTabStoreState {
  modalState: ModalState | null;
  isOpeningItem: boolean;
  isSaving: boolean;
}

interface PerTabStoreDependencies {
  getItemsByTab(): AdminRecordStore;
  replaceItemsByTab(itemsByTab: AdminRecordStore): void;
  setActiveTab(tabId: TabId): void;
  requestUpdate(): void;
}

interface UpdateHost {
  requestUpdate(): void;
}

export interface AdminStorage {
  loadAll(): Promise<AdminRecordStore>;
  read<T extends TabId>(tabId: T, id: IdString): Promise<AdminRecordStore[T][number] | null>;
  save<T extends TabId>(tabId: T, record: AdminRecordStore[T][number]): Promise<AdminRecordStore[T]>;
}

export type DraftChangeHandler<T = unknown> = (fieldKey: string, value: T) => void;
export type PendingRowsChangeHandler = (fieldKey: string, updater: (count: number) => number) => void;
export type PermissionRowsChangeHandler = (fieldKey: string, rows: PermissionRow[]) => void;
export type ResolverTitleChangeHandler = (value: string) => void;
export type OperationTriggerHandler = (fieldKey: string, message: string) => void;

interface FieldBlockProps {
  field: FieldDefinition;
  value: unknown;
  saved?: unknown;
  disabled?: boolean;
  useCardTitle?: boolean;
  store: PerTabStore;
}



interface MissingDependencyLine {
  value: string;
  missing: boolean;
}

interface SidebarSectionProps {
  title: string;
  content: JSX.Node;
}

interface ToggleFieldProps {
  field: FieldDefinition;
  value: boolean;
  onDraftChange: DraftChangeHandler<boolean>;
  headerOnly?: boolean;
}

interface RecordModalProps {
  store: PerTabStore;
}



export function isServerField(mode: Mode) {
  return ["create", "create edit", "edit", "server"].includes(mode);
}
function isAuthoredField(mode: Mode) {
  return ["create", "create edit", "edit"].includes(mode);
}

function isEditable(field: FieldDefinition, mode: ModalMode): boolean {
  if (!field.mode) return false;
  if (field.mode === "create edit") return true;
  if (field.mode === "create") return mode === "create";
  if (field.mode === "edit") return mode === "edit";
  if (field.mode === "create edit temp") return true;
  if (field.mode === "create temp") return mode === "create";
  if (field.mode === "edit temp") return mode === "edit";
  if (field.mode === "server") return false;
  const t: never = field.mode;
  return false;
}

function getPrimaryValue(tab: TabDefinition, item: AdminRecord): string {
  const primary = tab.columns[0] ?? tab.fields[0];
  return formatFieldValue(getAdminRecordValue(primary, item));
}

function getCreateLabel(tab: TabDefinition): string {
  return tab.createLabel;
}



function getFieldSection(field: FieldDefinition): FieldSection {
  return field.section ?? (isAuthoredField(field.mode) ? "authored" : "runtime");
}

function getSectionFields(tab: TabDefinition, section: FieldSection): FieldDefinition[] {
  // if (tab.id === "wikis" && section === "runtime") return [];
  // if (tab.id === "templates" && section === "runtime") return [];
  if (!tab.fieldGroups?.[section]?.length) return [];
  return tab.fields.filter((field) => {
    return getFieldSection(field) === section;
  });
}

function getSidebarFields(tab: TabDefinition) {
  const keys = new Set(tab.sidebarDisplay);
  return tab.fields.filter(e => keys.has(e.key));
}

function getFieldGroups(tab: TabDefinition, section: FieldSection, fields: FieldDefinition[]): FieldGroupDefinition[] {
  const fallback = fields.map((field) => ({ keys: [field.key], width: "half" as const }));

  const configuredGroups = tab.fieldGroups?.[section];
  if (!configuredGroups) return fallback;

  return configuredGroups.filter((group) => group.keys.some((key) => fields.some((field) => field.key === key)));
}


export function uniqueLines(values: readonly string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function formatStorageErrorForDisplay(storageError: string): string {
  if (!storageError) return storageError;

  try {
    const parsed = JSON.parse(storageError) as {
      reason?: unknown;
      status?: unknown;
      details?: { prettyErrors?: unknown };
    };
    const hasReason = typeof parsed.reason === "string";
    const hasStatus = typeof parsed.status === "number" || typeof parsed.status === "string";
    const prettyText = typeof parsed.details?.prettyErrors === "string"
      ? parsed.details.prettyErrors
      : "";

    if (hasReason && hasStatus && prettyText.trim()) return prettyText;
  } catch {
    // Keep the original storage error text when it's not valid JSON.
  }

  return storageError;
}

// #region - AppStore

class PerTabStoreImpl implements PerTabStore {
  private readonly state: PerTabStoreState = {
    modalState: null,
    isOpeningItem: false,
    isSaving: false,
  };

  constructor(
    private readonly storage: AdminStorage,
    private readonly deps: PerTabStoreDependencies,
  ) { }

  public get fieldState(): PerTabFieldState | null {
    return this.state.modalState as PerTabFieldState | null;
  }

  public get selectedTab(): TabDefinition | null {
    return this.fieldState ? getTab(this.fieldState.tabId) : null;
  }

  public get itemsByTab(): AdminRecordStore {
    return this.deps.getItemsByTab();
  }

  public get displayedStorageError(): string {
    return formatStorageErrorForDisplay(this.fieldState?.storageError ?? "");
  }

  public get isModalLoading(): boolean {
    return Boolean(this.fieldState?.loading);
  }

  public get isSaving(): boolean {
    return this.state.isSaving;
  }

  public get isOpeningItem(): boolean {
    return this.state.isOpeningItem;
  }

  public get isOpen(): boolean {
    return Boolean(this.fieldState);
  }

  public get isBusy(): boolean {
    return this.isOpeningItem || this.isSaving;
  }

  public readonly clearStorageError = () => {
    const modalState = this.state.modalState;
    if (!modalState?.storageError) return;
    this.patchState({
      modalState: {
        ...modalState,
        storageError: "",
      },
    });
  };

  public readonly closeModal = () => {
    if (!this.state.modalState) return;
    this.patchState({ modalState: null });
  };

  public readonly openCreate = (tabId: TabId) => {
    const draft = this.createDraftFor(tabId);
    this.deps.setActiveTab(tabId);
    this.patchState({
      modalState: this.createModalState(tabId, "create", draft, draft, false, ""),
    });
  };

  public readonly openItem = async (tabId: TabId, recordId: IdString) => {
    const emptyDraft = this.createDraftFor(tabId);
    this.deps.setActiveTab(tabId);
    this.patchState({
      modalState: this.createModalState(tabId, "edit", emptyDraft, emptyDraft, true, ""),
      isOpeningItem: true,
    });

    let item: AdminRecordStore[typeof tabId][number] | null = null;
    let storageError = "";
    try {
      item = await this.storage.read(tabId, recordId);
    } catch (error) {
      console.error(error);
      storageError = getErrorMessage(error, "Failed to load record details.");
    } finally {
      this.patchState({ isOpeningItem: false });
    }

    if (!item) {
      this.patchState({
        modalState: this.createModalState(
          tabId,
          "edit",
          emptyDraft,
          emptyDraft,
          false,
          storageError || "Record not found.",
        ),
      });
      return;
    }

    const draft = this.createDraftFor(tabId, item);
    this.patchState({
      modalState: this.createModalState(tabId, "edit", draft, draft, false, ""),
    });
  };

  public readonly updateDraft: DraftChangeHandler = (fieldKey, value) => {
    const modalState = this.state.modalState;
    if (!modalState) return;

    if (fieldKey === KeyFields[modalState.tabId] && typeof value === "string") {
      value = value;
    }

    const nextDraft = { ...modalState.draft } as typeof modalState.draft;
    (nextDraft as unknown as Record<string, unknown>)[fieldKey] = value;

    this.patchState({
      modalState: {
        ...modalState,
        draft: nextDraft,
      } as ModalState,
    });
  };

  public readonly updatePendingRows: PendingRowsChangeHandler = (fieldKey, updater) => {
    const modalState = this.state.modalState;
    if (!modalState) return;

    const nextCount = Math.max(0, updater(modalState.pendingRows[fieldKey] ?? 0));
    this.patchState({
      modalState: {
        ...modalState,
        pendingRows: {
          ...modalState.pendingRows,
          [fieldKey]: nextCount,
        },
      },
    });
  };

  public readonly updateTransientPermissionRows: PermissionRowsChangeHandler = (fieldKey, rows) => {
    const modalState = this.state.modalState;
    if (!modalState) return;

    this.patchState({
      modalState: {
        ...modalState,
        transientPermissionRows: {
          ...modalState.transientPermissionRows,
          [fieldKey]: rows,
        },
      },
    });
  };

  public readonly updateResolverTitle: ResolverTitleChangeHandler = (value) => {
    const modalState = this.state.modalState;
    if (!modalState) return;
    this.patchState({
      modalState: { ...modalState, resolverTitle: value },
    });
  };

  public readonly triggerOperation: OperationTriggerHandler = (fieldKey, message) => {
    const modalState = this.state.modalState;
    if (!modalState) return;
    this.patchState({
      modalState: {
        ...modalState,
        operationMessages: {
          ...modalState.operationMessages,
          [fieldKey]: message,
        },
      },
    });
  };

  public readonly saveDraft = async () => {
    const snapshot = this.state.modalState;
    if (!snapshot) return;

    this.patchState({
      modalState: {
        ...snapshot,
        storageError: "",
      },
      isSaving: true,
    });

    let savedTabItems: AdminRecordStore[typeof snapshot.tabId] | null = null;
    let storageError = "";
    try {
      savedTabItems = await this.storage.save(
        snapshot.tabId,
        snapshot.draft,
      );
    } catch (error) {
      console.error(error);
      storageError = getErrorMessage(error, "Failed to save record.");
    } finally {
      this.patchState({ isSaving: false });
    }

    if (!savedTabItems) {
      const currentModalState = this.state.modalState;
      if (!currentModalState) return;
      this.patchState({
        modalState: {
          ...currentModalState,
          storageError,
        },
      });
      return;
    }

    this.deps.replaceItemsByTab({
      ...this.itemsByTab,
      [snapshot.tabId]: savedTabItems,
    } as AdminRecordStore);
    this.patchState({ modalState: null });
  };

  private createDraftFor<T extends TabId>(tabId: T, source?: AdminRecordStore[T][number]): AdminRecordStore[T][number] {
    return createDraft(getTab(tabId), source) as AdminRecordStore[T][number];
  }

  private createModalState<T extends TabId>(
    tabId: T,
    mode: ModalMode,
    draft: AdminRecordStore[T][number],
    saved: AdminRecordStore[T][number],
    loading: boolean,
    storageError: string,
  ): ModalStateForTab<T> {
    return {
      tabId,
      mode,
      draft,
      saved,
      resolverTitle: "Docs/Welcome",
      operationMessages: {},
      pendingRows: {},
      transientPermissionRows: {},
      storageError,
      loading,
    } as ModalStateForTab<T>;
  }

  private patchState(patch: Partial<PerTabStoreState>): void {
    Object.assign(this.state, patch);
    this.deps.requestUpdate();
  }
}

class AppStore {
  public readonly state: AppStoreState = {
    activeTab: "wikis",
    itemsByTab: getEmptyItems(),
    isLoadingData: true,
  };

  public readonly perTabStore: PerTabStore;

  private hasLoaded = false;
  private loadPromise: Promise<void> | null = null;

  constructor(
    private readonly host: UpdateHost,
    private readonly storage: AdminStorage,
  ) {
    this.perTabStore = new PerTabStoreImpl(storage, {
      getItemsByTab: () => this.state.itemsByTab,
      replaceItemsByTab: (itemsByTab) => this.patchState({ itemsByTab }),
      setActiveTab: (tabId) => this.setActiveTab(tabId),
      requestUpdate: () => this.host.requestUpdate(),
    });
  }

  public get currentTab(): TabDefinition {
    return getTab(this.state.activeTab);
  }

  public get activeTabItems(): AdminRecordStore[TabId] {
    return this.state.itemsByTab[this.state.activeTab];
  }

  public get isListInteractionDisabled(): boolean {
    return this.perTabStore.isBusy;
  }

  public ensureLoaded(): Promise<void> {
    if (this.hasLoaded) return Promise.resolve();
    if (!this.loadPromise) {
      this.loadPromise = this.loadAllInternal().finally(() => {
        this.loadPromise = null;
      });
    }
    return this.loadPromise;
  }

  public reload(): Promise<void> {
    this.hasLoaded = false;
    return this.ensureLoaded();
  }

  public readonly setActiveTab = (tabId: TabId) => {
    if (tabId === this.state.activeTab) return;
    this.patchState({ activeTab: tabId });
  };

  public readonly openCreate = (tabId: TabId) => {
    this.perTabStore.openCreate(tabId);
  };

  public readonly openItem = async (tabId: TabId, recordId: IdString) => {
    await this.perTabStore.openItem(tabId, recordId);
  };

  private async loadAllInternal(): Promise<void> {
    this.patchState({
      isLoadingData: true,
    });

    try {
      const loadedItems = await this.storage.loadAll();
      this.hasLoaded = true;
      this.patchState({
        itemsByTab: loadedItems,
        isLoadingData: false,
      });
    } catch (error) {
      this.patchState({
        isLoadingData: false,
      });
      throw error;
    }
  }

  private patchState(patch: Partial<AppStoreState>): void {
    Object.assign(this.state, patch);
    this.host.requestUpdate();
  }
}



// #region - field block

@customElement("mws-field-block")
class FieldBlockElement<T> extends JSXElement {
  useLightDOM: boolean = true;

  @state() accessor props!: FieldBlockProps;

  protected render() {
    const { field, useCardTitle, value, saved: savedValue, store } = this.props;
    const fieldState = store.fieldState;
    if (!fieldState) return null;

    const modalMode = fieldState.mode;
    const editable = isEditable(field, modalMode);
    const disabled = Boolean(this.props.disabled) || !editable;
    // const useToggleEditor = editable && field.key === "requiredPluginsEnabled";
    const isToggle = field.type === "switch";
    const children = <>
      {renderFieldEditor({
        inputId: `field-${field.key}`,
        field,
        value,
        disabled,
        fieldState,
        itemsByTab: store.itemsByTab,
        onDraftChange: store.updateDraft,
        onPendingRowsChange: store.updatePendingRows,
        onTransientPermissionRowsChange: store.updateTransientPermissionRows,
        onResolverTitleChange: store.updateResolverTitle,
        onTriggerOperation: store.triggerOperation,
      })}
    </>;
    return (
      <div class="field-block">
        <div class="field-editor">
          {!useCardTitle ? <label class="field-label" for={`field-${field.key}`}>{field.label}</label> : null}
          {field.description ? <p class="field-helper">{field.description}</p> : null}
          {isToggle ? <div class="toggle-field-row">{children}</div> : children}
        </div>
      </div>
    );
  }
}

export function getAdminRecordValue(field: FieldDefinition | ColumnDefinition, draft: AdminRecord) {
  if (!(field.key in draft)) throw new Error("The field " + field.key + " is not defined in the draft record");
  return (draft as any)[field.key];
}
export function setAdminRecordValue(field: FieldDefinition | ColumnDefinition, draft: AdminRecord, value: unknown, init: boolean) {
  if (!init && !(field.key in draft)) throw new Error("The field " + field.key + " is not defined in the draft record");
  (draft as any)[field.key] = value;
}

function sidebarField(field: FieldDefinition, draft: AdminRecord, saved: AdminRecord, itemsByTab?: AdminRecordStore) {
  const value = getAdminRecordValue(field, saved);
  return sidebarSection({
    title: field.label,
    content: renderFieldSidebar({
      field,
      value: getAdminRecordValue(field, saved),
      itemsByTab
    })
  })
}
function sidebarSection({ title, content }: SidebarSectionProps) {
  return (
    <div class="sidebar-section">
      <h4 class="sidebar-section-title">{title}</h4>
      <div class="sidebar-section-body">{content}</div>
    </div>
  );
}

// #region tab modal

@customElement("mws-record-modal")
class RecordModalElement extends JSXElement {
  useLightDOM: boolean = true;

  @state() accessor props!: RecordModalProps;

  protected render() {
    const { store } = this.props;
    const { selectedTab, fieldState } = store;
    if (!selectedTab || !fieldState) return null;

    const itemsByTab = store.itemsByTab;
    const storageError = store.displayedStorageError;
    const isModalLoading = store.isModalLoading;
    const isSaving = store.isSaving;
    const isOpeningItem = store.isOpeningItem;
    const onClose = store.closeModal;
    const onSave = store.saveDraft;
    const onDraftChange = store.updateDraft;
    const onClearStorageError = store.clearStorageError;
    const recordTitle = fieldState.mode === "create"
      ? `New ${selectedTab.label.slice(0, -1)}`
      : getPrimaryValue(selectedTab, fieldState.draft) || `${selectedTab.label.slice(0, -1)} details`;
    const headerCopy = storageError
      ? "Storage returned an error for this record. Fix the issue or close the dialog and try again."
      : selectedTab.description;

    const authoredFields = !isModalLoading ? getSectionFields(selectedTab, "authored") : [];
    const runtimeFields = !isModalLoading ? getSectionFields(selectedTab, "runtime") : [];
    const operationFields = !isModalLoading ? getSectionFields(selectedTab, "operations") : [];
    const sidebarFields = !isModalLoading ? getSidebarFields(selectedTab) : [];

    return (
      <div class="modal-shell" webjsx-attr-open onclick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
        <section class="modal-card" role="dialog" aria-modal="true" aria-label={`${selectedTab.label} details`}>
          <header class="modal-header">
            <div class="modal-title">
              <p class="eyebrow">{selectedTab.eyebrow}</p>
              <h3>{isModalLoading
                ? `Loading ${selectedTab.label.slice(0, -1).toLowerCase()}...`
                : textWithSlashes(recordTitle)}</h3>
              <p>{isModalLoading ? "Fetching record details from async storage before rendering the form." : headerCopy}</p>
            </div>
            <div class="close-button" onclick={onClose} aria-label="Close details">
              <MaterialSymbol icon={closeIcon} />
            </div>
          </header>

          {isModalLoading ? (
            <div class="modal-loading-shell">
              <div class="modal-loading-bar" aria-hidden="true"><span></span></div>
              <p class="modal-loading-copy">Loading {selectedTab.label.toLowerCase()} details...</p>
            </div>
          ) : (
            <div class="modal-layout">
              <aside class="field-index modal-sidebar">
                {sidebarFields.map(field => sidebarSection({
                  title: field.label,
                  content: renderFieldSidebar({
                    field,
                    value: getAdminRecordValue(field, fieldState.saved),
                    itemsByTab
                  })
                }))}
              </aside>

              <div class="field-stack modal-main">
                {([
                  ["authored", authoredFields],
                  ["runtime", runtimeFields],
                  ["operations", operationFields],
                ] as [FieldSection, FieldDefinition[]][]).map(([section, fields]) => {
                  if (!fields.length) return null;
                  const heading = getSectionHeading(section, fieldState.mode);
                  return (
                    <section class="modal-section">
                      {heading ? (
                        <header class="modal-section-header">
                          <div>
                            <h4>{heading.title}</h4>
                          </div>
                          <p>{heading.copy}</p>
                        </header>
                      ) : null}

                      <div class="section-field-grid">
                        {getFieldGroups(selectedTab, section, fields).map((group) => {
                          const groupFields = group.keys
                            .map((key) => fields.find((field) => field.key === key))
                            .filter((field): field is FieldDefinition => Boolean(field));
                          const headerField = group.headerFieldKey
                            ? fields.find((field) => field.key === group.headerFieldKey)
                            : undefined;
                          const groupDisabled = Boolean(group.disabledWhenHeaderOff && headerField && getAdminRecordValue(headerField, fieldState.draft) !== true);
                          const headerDescription = group.description ?? (!group.footerDescriptionFromHeader ? headerField?.description : undefined) ?? "";
                          const footerDescription = group.footerDescriptionFromHeader ? headerField?.description : undefined;
                          if (!groupFields.length) return null;

                          return (
                            <article class={group.width === "full" ? "field-card is-full" : "field-card"}>
                              <header class="field-card-header">
                                <div class="field-card-header-row">
                                  <h4>{group.title ?? (groupFields.length === 1 ? groupFields[0].label : groupFields.map((field) => field.label).join(" and "))}</h4>
                                  {headerField ? renderSwitchField({
                                    field: headerField,
                                    value: getAdminRecordValue(headerField, fieldState.draft) ?? "",
                                    onDraftChange,
                                  }, true) : null}
                                </div>
                                {headerDescription ? <p>{headerDescription}</p> : null}
                              </header>

                              <div class={groupFields.length > 1 ? (group.layout === "stack" ? "composite-fields is-stack" : "composite-fields") : "single-field"}>
                                {groupFields.map((field) => (
                                  <FieldBlockElement
                                    field={field}
                                    value={getAdminRecordValue(field, fieldState.draft)}
                                    saved={getAdminRecordValue(field, fieldState.saved)}
                                    disabled={groupDisabled}
                                    useCardTitle={groupFields.length === 1}
                                    store={store}
                                  />
                                ))}
                              </div>
                              {footerDescription ? <div class="field-card-footer-note"><p>{footerDescription}</p></div> : null}
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}

                {true
                  ? <footer class="modal-actions">
                    <button class="ghost-button" type="button" onclick={onClose} disabled={isSaving}>Cancel</button>
                    <button class="primary-button" type="button" onclick={onSave} disabled={isSaving || isOpeningItem}>{isSaving ? "Saving..." : fieldState.mode === "create" ? `Save ${selectedTab.label.slice(0, -1)}` : "Save changes"}</button>
                  </footer>
                  : <footer class="modal-actions">
                    <button class="ghost-button" type="button" onclick={onClose} disabled={isSaving}>Close</button>
                  </footer>}
              </div>
            </div>
          )}

          {storageError ? (
            <footer class="modal-header" role="alert" aria-live="polite">
              <pre style="white-space: break-spaces;">{storageError}</pre>
              <button class="error-close-button" type="button" onclick={onClearStorageError} aria-label="Dismiss error message">
                <MaterialSymbol icon={closeIcon} />
              </button>
            </footer>
          ) : null}
        </section>
      </div>
    );
  }
}

// #region tab main
function throttle(window: number) {
  let active = false;
  return () => {
    if (!active) {
      active = true;
      setTimeout(() => {
        active = false;
      }, window);
      return false;
    } else {
      return active;
    }
  }
}

@addstyles(css)
@customElement("mws-app")
export class App extends JSXElement {
  // don't use shadow dom. allows inheriting main.css styles.
  useLightDOM: boolean = true;

  @state() accessor mainStorageError = "";

  private readonly store = new AppStore(this, adminStorage);
  private readonly handlePageShow = () => {
    void this.loadAdminRecords(true);
  };
  private readonly handleAccountMenuClick = (event: MouseEvent) => {
    const accountMenu = this.querySelector(".hero-account-menu");
    if (!(accountMenu instanceof HTMLDetailsElement) || !accountMenu.open) return;

    const target = event.target;
    if (target instanceof Node && accountMenu.contains(target)) return;
    accountMenu.open = false;
  };

  constructor() {
    super()
  }

  private readonly clearMainStorageError = () => {
    if (!this.mainStorageError) return;
    this.mainStorageError = "";
  };
  loadThrottle = throttle(3000);
  private readonly loadAdminRecords = async (reload = false) => {
    if (this.loadThrottle()) return;
    this.clearMainStorageError();

    try {
      if (reload) {
        await this.store.reload();
        return;
      }

      await this.store.ensureLoaded();
    } catch (error) {
      console.error(error);
      this.mainStorageError = formatStorageErrorForDisplay(
        getErrorMessage(error, "Failed to load admin records."),
      );
    }
  };

  private readonly openCreate = (tabId: TabId) => {
    this.store.openCreate(tabId);
  };

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this.handleAccountMenuClick, true);
    window.addEventListener("pageshow", this.handlePageShow);
    void this.loadAdminRecords();
  }

  disconnectedCallback(): void {
    document.removeEventListener("click", this.handleAccountMenuClick, true);
    window.removeEventListener("pageshow", this.handlePageShow);
    super.disconnectedCallback();
  }

  protected render() {
    const store = this.store;
    const { activeTab, itemsByTab, isLoadingData } = store.state;
    const perTabStore = store.perTabStore;
    const currentTab = store.currentTab;
    const activeTabItems = store.activeTabItems;
    const isListInteractionDisabled = store.isListInteractionDisabled;
    const mainStorageError = this.mainStorageError;

    return (
      <div class="admin-shell">
        <header class="hero-panel">
          <div class="hero-panel-content">
            <p class="eyebrow">Multi-wiki server administration</p>
            <h1 style="display:flex; gap: 1rem; align-items:center;">
              <span>MWS</span>
            </h1>
            <p class="hero-copy">All your thoughts, in as many places as you need them.</p>
          </div>
          <div class="hero-account-shell">
            <a href={pathPrefix + "/tw5/"} class="hero-account-trigger">
              <span class="hero-account-name">{embeddedServerResponse.tw5Version}</span>
              <MaterialSymbol icon={tw5logo} class="hero-account-icon" />
            </a>
            <details class="hero-account-menu">
              <summary class="hero-account-trigger" aria-label="Open account menu">
                <span class="hero-account-name">{embeddedServerResponse.userState.username}</span>
                <span class="hero-account-icon" aria-hidden="true">
                  {/* <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/00/Crab_Nebula.jpg/250px-Crab_Nebula.jpg"/> */}
                  {embeddedServerResponse.userState.avatarUrl
                    ? <img src={embeddedServerResponse.userState.avatarUrl} />
                    : <MaterialSymbol icon={accountCircleIcon} />}
                </span>
              </summary>
              <div class="hero-account-dropdown" role="menu" aria-label="Account options">
                <button
                  class="hero-account-action"
                  type="button"
                  role="menuitem"
                  onclick={() => {
                    location.pathname = pathPrefix + "/profile";
                  }}
                >
                  Profile
                </button>
                <button
                  class="hero-account-action"
                  type="button"
                  role="menuitem"
                  onclick={logout}
                >
                  Logout
                </button>
              </div>
            </details>
          </div>
        </header>

        <nav class="tab-strip" aria-label="Admin sections">
          {getAllTabs().map((tab) => (
            <button
              class={tab.id === activeTab ? "tab-button is-active" : "tab-button"}
              onclick={() => store.setActiveTab(tab.id)}
              type="button"
            >
              <span>{tab.label}</span>
              <small>{itemsByTab[tab.id].length} items</small>
            </button>
          ))}
        </nav>

        <section class="section-header">
          <div>
            <p class="eyebrow">{currentTab.eyebrow}</p>
            <h2>{currentTab.label}</h2>
          </div>
          <p class="section-copy">{currentTab.description}</p>
          <button
            class="primary-button"
            type="button"
            onclick={() => this.openCreate(currentTab.id)}
            disabled={isLoadingData || perTabStore.isOpeningItem || perTabStore.isSaving}
          >{getCreateLabel(currentTab)}</button>
        </section>

        <section class="list-panel">
          {/* <div class="list-toolbar">
            <div>
              <strong>{currentTab.label} list</strong>
              <p>{isLoadingData
                ? "Loading…"
                : "Click any row to for more information."}</p>
            </div>
            <div class="toolbar-actions">
              <button
                class="ghost-button"
                type="button"
                onclick={() => this.openCreate(currentTab.id)}
                disabled={isLoadingData || perTabStore.isOpeningItem || perTabStore.isSaving}
              >{getCreateLabel(currentTab)}</button>
            </div>
          </div> */}

          {mainStorageError ? (
            <div class="field-callout" role="alert" aria-live="polite">
              <pre style="white-space: break-spaces;">{mainStorageError}</pre>
              <button class="ghost-button" type="button" onclick={this.clearMainStorageError}>Dismiss</button>
            </div>
          ) : null}

          <div class="list-grid list-grid-header">
            {currentTab.columns.map((column) => (
              <div class="list-cell list-head" style={
                column.width && column.width > 1
                  ? { gridColumn: "span " + column.width }
                  : {}
              }>{column.label}</div>
            ))}

          </div>

          <div class="list-body">
            {isLoadingData ? (
              <div class="field-callout">
                <p>Loading {currentTab.label.toLowerCase()}…</p>
              </div>
            ) : activeTabItems.length ? activeTabItems.map((item) => (
              <div
                class="list-grid list-row"
                role="button"
                tabindex={isListInteractionDisabled ? -1 : 0}
                aria-disabled={isListInteractionDisabled ? "true" : undefined}
                onclick={() => {
                  if (!isListInteractionDisabled)
                    void store.openItem(currentTab.id, item.id);
                }}
                onkeydown={(event) => {
                  if (isListInteractionDisabled) return;
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  void store.openItem(currentTab.id, item.id);
                }}
              >
                {currentTab.columns.map((column) => {
                  const value = getAdminRecordValue(column, item);
                  const isFirstColumn = column.key === currentTab.columns[0]?.key;
                  const linkUrl = isFirstColumn ? getListColumnLink(currentTab.id, column.key, item) : null;
                  if (typeof linkUrl === "string") {
                    return <a
                      class="list-cell list-cell-link"
                      href={linkUrl}
                      onclick={(event) => event.stopPropagation()}
                      onkeydown={(event) => event.stopPropagation()}
                      style={column.width && column.width > 1
                        ? { gridColumn: "span " + column.width }
                        : {}
                      }
                    >{renderListCellValue(column.key, value)}</a>;
                  }
                  return (
                    <div class="list-cell" style={
                      column.width && column.width > 1
                        ? { gridColumn: "span " + column.width }
                        : {}
                    }>
                      {renderListCellValue(column.key, value)}
                      {/* {(() => {


                        return typeof linkUrl === "string"
                          ? <a
                            class="list-cell-link"
                            href={linkUrl} onclick={(event) => event.stopPropagation()}
                            onkeydown={(event) => event.stopPropagation()}
                          >{renderListCellValue(column.key, value)}</a>
                          : renderListCellValue(column.key, value);
                      })()} */}
                    </div>
                  )
                })}
              </div>
            )) : (
              <div class="field-callout">
                <p>Create a {currentTab.label.toLowerCase()} to get started.</p>
              </div>
            )}
          </div>
        </section>

        {perTabStore.isOpen ? (
          <RecordModalElement
            store={perTabStore}
          />
        ) : null}
      </div>
    );
  }
}

// #region table stuff

function renderListCellValue(columnKey: string, value: string | undefined) {
  const formattedValue = formatFieldValue(value);

  if (columnKey === "statusFlags" && value?.toLowerCase().includes("alert")) {
    return (
      <span class="missing-marker" aria-label={formattedValue} title={formattedValue}>
        <MaterialSymbol icon={warningIcon} />
      </span>
    );
  }

  return textWithSlashes(formattedValue);
}

function getListColumnLink(tabId: TabId, columnKey: string, item: AdminRecord): string | null {
  const mapper = getListColumnLinkMappers(tabId)[columnKey];
  return mapper ? mapper(item) : null;
}


function getListColumnLinkMappers(tabId: TabId): Partial<Record<string, ListColumnLinkMapper>> {
  switch (tabId) {
    case "wikis":
      return {
        slug: (item) => {
          definitely<WikiAdminRecord>(item);
          return item.slug ? `${pathPrefix}/wiki/${encodeURIComponent(item.slug)}` : null;
        },
      };
    case "templates":
    case "bags":
    case "roles":
    case "users":
      return {};
    default: {
      const exhaustive: never = tabId;
      return exhaustive;
    }
  }
}

type ListColumnLinkMapper = (item: AdminRecord) => string | null;
